import { Video, MessageCircle, Zap, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface SuggestedNextStepProps {
  matches: Array<{
    id: string;
    match_source: string;
    matchedProfile: {
      id: string;
      name: string;
      photo_url: string | null;
    } | null;
    isOnline?: boolean;
    compatibility?: {
      score: number;
    };
    created_at: string;
  }>;
  onVideoChat: (matchId: string) => void;
  onMessage: (matchId: string) => void;
  onPortal: (matchId: string) => void;
  className?: string;
}

export const SuggestedNextStep = ({
  matches,
  onVideoChat,
  onMessage,
  onPortal,
  className,
}: SuggestedNextStepProps) => {
  const suggestions = useMemo(() => {
    if (matches.length === 0) return [];

    // Prioritize: online + high compatibility + recent
    const prioritized = matches
      .filter((m) => m.matchedProfile)
      .map((match) => {
        const daysAgo = (Date.now() - new Date(match.created_at).getTime()) / (1000 * 60 * 60 * 24);
        const score =
          (match.isOnline ? 30 : 0) +
          (match.compatibility?.score || 0) * 0.5 +
          (daysAgo <= 1 ? 20 : daysAgo <= 7 ? 10 : 0);
        return { match, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(({ match }) => match);

    return prioritized.map((match) => {
      if (!match.matchedProfile) return null;

      const isExhibition = match.match_source === "exhibition";
      const isGlobal = match.match_source === "global_connect";
      const isOnline = match.isOnline;
      const hasHighCompatibility = (match.compatibility?.score || 0) >= 70;

      // Determine best action
      let action: "video" | "message" | "portal" = "message";
      let label = "";
      let icon = MessageCircle;
      let onClick = () => onMessage(match.id);

      if (isOnline && (isExhibition || isGlobal)) {
        action = "video";
        label = `Video Chat with ${match.matchedProfile.name.split(" ")[0]}`;
        icon = Video;
        onClick = () => onVideoChat(match.id);
      } else if (hasHighCompatibility && isOnline) {
        action = "message";
        label = `Message ${match.matchedProfile.name.split(" ")[0]}`;
        icon = MessageCircle;
        onClick = () => onMessage(match.id);
      } else if (match.match_source === "pop_balloon") {
        action = "portal";
        label = `Open Portal with ${match.matchedProfile.name.split(" ")[0]}`;
        icon = Zap;
        onClick = () => onPortal(match.id);
      } else {
        action = "message";
        label = `Continue chatting with ${match.matchedProfile.name.split(" ")[0]}`;
        icon = MessageCircle;
        onClick = () => onMessage(match.id);
      }

      return {
        match,
        action,
        label,
        icon,
        onClick,
        isOnline,
        compatibility: match.compatibility?.score || 0,
      };
    }).filter(Boolean) as Array<{
      match: typeof matches[0];
      action: "video" | "message" | "portal";
      label: string;
      icon: typeof Video;
      onClick: () => void;
      isOnline: boolean;
      compatibility: number;
    }>;
  }, [matches, onVideoChat, onMessage, onPortal]);

  if (suggestions.length === 0) return null;

  return (
    <div className={cn("mb-4", className)}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Suggested Next Step</h3>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.match.id}
            onClick={suggestion.onClick}
            className={cn(
              "shrink-0 flex flex-col gap-2 p-4 rounded-xl border-2 transition-all",
              "bg-card/80 backdrop-blur-sm min-w-[160px]",
              suggestion.action === "video"
                ? "border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/5"
                : suggestion.action === "portal"
                ? "border-primary/30 hover:border-primary/50 hover:bg-primary/5"
                : "border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/5",
              "animate-fade-in-up"
            )}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center justify-between">
              <suggestion.icon
                className={cn(
                  "w-5 h-5",
                  suggestion.action === "video"
                    ? "text-amber-500"
                    : suggestion.action === "portal"
                    ? "text-primary"
                    : "text-blue-500"
                )}
              />
              {suggestion.isOnline && (
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              )}
            </div>
            <p className="text-xs font-medium text-foreground text-left leading-tight">
              {suggestion.label}
            </p>
            {suggestion.compatibility > 0 && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <span>{suggestion.compatibility}% match</span>
              </div>
            )}
            <ArrowRight
              className={cn(
                "w-3 h-3 self-end mt-auto",
                suggestion.action === "video"
                  ? "text-amber-500"
                  : suggestion.action === "portal"
                  ? "text-primary"
                  : "text-blue-500"
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

